/**
 * PR Pipeline — read-only cross-stage view of every PR in the system.
 *
 * Why this page exists
 * --------------------
 * Today the dashboard shows only PRs *you can act on* (filtered by role + emp_no
 * assignment). When someone asks "where is PR 119xxx?" there's no good answer.
 * This page fills that gap: any logged-in user can land here and see, at a
 * glance, which workflow stage each PR is in, who it's pending with, and what
 * AI signals it's carrying. No role gating — fully read-only.
 *
 * Data shape (from /api/prs/pipeline)
 * ------------------------------------
 *   {
 *     rows: [{ voc_no, vdate, amount, currency, supplier_name,
 *              requestor_name, auth_by_name, app_by_name,
 *              stage, stage_label, stage_color, pending_with,
 *              ai_action, hold_info, ... }],
 *     counts: { ALL, REQUESTOR_REVIEW, L1_AUTHORISATION, L2_APPROVAL,
 *               ON_HOLD, COMPLETED, REJECTED, REJECTED_BY_REQUESTOR }
 *   }
 *
 * Filters are server-side. Tabs trigger a re-fetch with `?stage=...`.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { auth } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Search, RefreshCw, Clock, ArrowUp, ArrowDown, ArrowUpDown, AlertTriangle } from "lucide-react";
import PRDetailDrawer from "@/components/PRDetailDrawer";
import MirrorFreshness from "@/components/MirrorFreshness";

const STAGE_TABS = [
  { code: "ALL", label: "All", color: "slate" },
  { code: "REQUESTOR_REVIEW", label: "Requestor Review", color: "violet" },
  { code: "L1_AUTHORISATION", label: "L1 Authorise", color: "blue" },
  { code: "L2_APPROVAL", label: "L2 Approve", color: "cyan" },
  { code: "ON_HOLD", label: "On Hold", color: "amber" },
  { code: "COMPLETED", label: "Completed", color: "emerald" },
  { code: "REJECTED", label: "Rejected", color: "rose" },
];

// Tailwind-static colour map. We can't inline `bg-${color}-100` because Tailwind
// can't infer dynamic classes at build time. So map each stage colour to a
// concrete utility string here.
const COLOR_MAP = {
  slate:   { bg: "bg-slate-100",   text: "text-slate-700",   border: "border-slate-300",   pill: "bg-slate-700 text-white" },
  violet:  { bg: "bg-violet-100",  text: "text-violet-700",  border: "border-violet-300",  pill: "bg-violet-700 text-white" },
  blue:    { bg: "bg-blue-100",    text: "text-blue-700",    border: "border-blue-300",    pill: "bg-blue-700 text-white" },
  cyan:    { bg: "bg-cyan-100",    text: "text-cyan-800",    border: "border-cyan-300",    pill: "bg-cyan-700 text-white" },
  amber:   { bg: "bg-amber-100",   text: "text-amber-800",   border: "border-amber-300",   pill: "bg-amber-700 text-white" },
  emerald: { bg: "bg-emerald-100", text: "text-emerald-800", border: "border-emerald-300", pill: "bg-emerald-700 text-white" },
  rose:    { bg: "bg-rose-100",    text: "text-rose-800",    border: "border-rose-300",    pill: "bg-rose-700 text-white" },
};

function fmtMoney(amount, currency) {
  if (amount == null) return "—";
  const n = Number(amount);
  if (!Number.isFinite(n)) return String(amount);
  return `${currency || "INR"} ${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
  } catch { return iso.slice(0, 10); }
}

// Stage badge — module-scoped so React doesn't see a new component type on
// every parent render (lint rule react/no-unstable-nested-components).
function StageBadge({ code, label, color }) {
  const c = COLOR_MAP[color] || COLOR_MAP.slate;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider ${c.bg} ${c.text} border ${c.border}`}
      data-testid={`stage-badge-${code}`}
    >
      {label}
    </span>
  );
}

// Sortable column header. Click to cycle asc → desc → off. Shows an arrow icon
// reflecting the current sort state on this column (faded chevrons for inactive
// columns so users discover the affordance without clutter).
function SortableTh({ label, k, sort, onClick, align = "left" }) {
  const active = sort.key === k;
  const Icon = !active ? ArrowUpDown : sort.dir === "desc" ? ArrowDown : ArrowUp;
  return (
    <th
      onClick={() => onClick(k)}
      data-testid={`pipeline-th-${k}`}
      data-sort-active={active ? sort.dir : "off"}
      className={`px-3 py-2.5 select-none cursor-pointer hover:text-cyan-700 transition-colors ${align === "right" ? "text-right" : ""}`}
    >
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "justify-end w-full" : ""}`}>
        {label}
        <Icon size={11} className={active ? "text-cyan-700" : "text-slate-300"} />
      </span>
    </th>
  );
}

export default function Pipeline() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const stage = params.get("stage") || "ALL";
  const aiHold = params.get("ai_hold") === "1";
  const [q, setQ] = useState(params.get("q") || "");
  const [requestor, setRequestor] = useState(params.get("requestor") || "");
  const [creator, setCreator] = useState(params.get("creator") || "");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openVoc, setOpenVoc] = useState(null);
  const [syncInfo, setSyncInfo] = useState(null);
  // Client-side sorting. Server already returns rows sorted by recent date desc;
  // user can click a column header to override. Clicking the same header twice
  // toggles asc/desc. Clicking a third time returns to server's default order.
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const user = auth.user() || {};

  // Load ALL rows ONCE (no server-side filters except stage='ALL' to get everything).
  // Stage tab, q, requestor, creator filtering all happen client-side below —
  // typing in any input is instant, no server round-trip.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/pipeline");
      setRows(r.data?.rows || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Client-side filtering — applies stage tab + q + requestor + creator over
  // the already-loaded rows. Results refresh on every keystroke (no debounce
  // needed because filtering 300 in-memory rows takes <1ms).
  const filteredRows = useMemo(() => {
    let out = rows;
    if (stage && stage !== "ALL") out = out.filter((r) => r.stage === stage);
    if (q) {
      const ql = q.trim().toLowerCase();
      out = out.filter((r) =>
        String(r.voc_no).toLowerCase().includes(ql) ||
        (r.supplier_name || "").toLowerCase().includes(ql),
      );
    }
    if (requestor) {
      const rl = requestor.trim().toLowerCase();
      out = out.filter((r) =>
        (r.requestor_name || "").toLowerCase().includes(rl) ||
        (r.requestor || "").toLowerCase().includes(rl),
      );
    }
    if (creator) {
      const cl = creator.trim().toLowerCase();
      out = out.filter((r) =>
        (r.create_by || "").toLowerCase().includes(cl) ||
        (r.create_by_name || "").toLowerCase().includes(cl),
      );
    }
    return out;
  }, [rows, stage, q, requestor, creator]);

  // Recompute stage counts from the currently-visible rows (after q/requestor/
  // creator filters but BEFORE the stage tab filter — so each tab badge shows
  // how many would land in that bucket given the current text filters).
  const stageCounts = useMemo(() => {
    const base = rows.filter((r) => {
      if (q) {
        const ql = q.trim().toLowerCase();
        if (!String(r.voc_no).toLowerCase().includes(ql) && !(r.supplier_name || "").toLowerCase().includes(ql)) return false;
      }
      if (requestor) {
        const rl = requestor.trim().toLowerCase();
        if (!(r.requestor_name || "").toLowerCase().includes(rl) && !(r.requestor || "").toLowerCase().includes(rl)) return false;
      }
      if (creator) {
        const cl = creator.trim().toLowerCase();
        if (!(r.create_by || "").toLowerCase().includes(cl) && !(r.create_by_name || "").toLowerCase().includes(cl)) return false;
      }
      return true;
    });
    const c = { ALL: base.length };
    for (const r of base) c[r.stage] = (c[r.stage] || 0) + 1;
    return c;
  }, [rows, q, requestor, creator]);

  // Unique supplier / requestor / creator names from the loaded rows —
  // powers the <datalist> autocomplete on each input.
  const supplierSuggestions = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.supplier_name && s.add(r.supplier_name));
    return Array.from(s).sort();
  }, [rows]);
  const requestorSuggestions = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.requestor_name && s.add(r.requestor_name));
    return Array.from(s).sort();
  }, [rows]);
  const creatorSuggestions = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.create_by_name && s.add(r.create_by_name));
    return Array.from(s).sort();
  }, [rows]);

  // Lightweight mirror status poll so the freshness pill stays accurate.
  useEffect(() => {
    let cancelled = false;
    const f = async () => {
      try {
        const r = await api.get("/mirror/status");
        if (!cancelled) setSyncInfo(r.data);
      } catch (err) {
        // Non-fatal — mirror status is just a UX hint
        console.warn("[Pipeline] /mirror/status fetch failed", err);
      }
    };
    f();
    const id = setInterval(f, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const setStage = (s) => {
    const next = new URLSearchParams(params);
    if (s === "ALL") next.delete("stage"); else next.set("stage", s);
    setParams(next, { replace: true });
  };

  const clearFilters = () => {
    setQ(""); setRequestor(""); setCreator("");
    const next = new URLSearchParams();
    if (stage !== "ALL") next.set("stage", stage);
    setParams(next, { replace: true });
  };

  // Keep the URL query-string in sync with the active filters so that
  // refreshing the page / sharing the URL preserves the search.
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (q) next.set("q", q); else next.delete("q");
    if (requestor) next.set("requestor", requestor); else next.delete("requestor");
    if (creator) next.set("creator", creator); else next.delete("creator");
    setParams(next, { replace: true });
  }, [q, requestor, creator]);

  // "Just mine" — one-click filter to my own emp_no as creator. Useful when a
  // user wants to immediately see only the PRs they personally raised.
  const setJustMine = () => {
    const empNo = (user?.emp_no || "").trim();
    if (!empNo) return;
    setCreator(empNo);
    const next = new URLSearchParams(params);
    next.set("creator", empNo);
    setParams(next, { replace: true });
  };
  const justMineActive = creator && user?.emp_no && creator.trim() === user.emp_no.trim();

  // Count of AI-flagged HOLD recommendations across all currently-loaded rows.
  // These are PRs where the deterministic risk engine recommended HOLD but no
  // human has acted — they still sit in their real stage (L1/L2/Requestor),
  // not in the ON_HOLD bucket. The chip below lets users surface them.
  const aiHoldCount = useMemo(
    () => filteredRows.filter((r) => r.ai_action === "HOLD").length,
    [rows],
  );

  const visibleRows = useMemo(() => {
    let base = aiHold ? filteredRows.filter((r) => r.ai_action === "HOLD") : filteredRows;
    if (!sort.key) return base;
    // Type-aware accessor — numeric for voc_no & amount, date-like ISO string
    // for vdate, alphanum for everything else.
    const get = (r) => {
      switch (sort.key) {
        case "voc_no":         return Number(r.voc_no) || 0;
        case "vdate":          return r.vdate || "";
        case "supplier_name":  return (r.supplier_name || "").toLowerCase();
        case "plant_name":     return (r.plant_name || r.plant || "").toLowerCase();
        case "amount":         return Number(r.amount) || 0;
        case "requestor_name": return (r.requestor_name || r.create_by_name || "").toLowerCase();
        case "stage_label":    return (r.stage_label || "").toLowerCase();
        case "pending_with":   return (r.pending_with || "").toLowerCase();
        default:               return "";
      }
    };
    const factor = sort.dir === "desc" ? -1 : 1;
    return [...base].sort((a, b) => {
      const va = get(a); const vb = get(b);
      if (va < vb) return -1 * factor;
      if (va > vb) return  1 * factor;
      return 0;
    });
  }, [filteredRows, sort, aiHold]);

  const toggleAiHold = () => {
    const next = new URLSearchParams(params);
    if (aiHold) next.delete("ai_hold"); else next.set("ai_hold", "1");
    setParams(next, { replace: true });
  };

  // Click a header to set/toggle sort. Three states: asc → desc → off.
  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return { key: null, dir: "asc" };
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-gradient-to-r from-cyan-900 to-cyan-800 text-white shadow-lg sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-2.5 sm:py-3 flex items-center gap-2 sm:gap-3">
          <Button
            variant="ghost" size="sm" onClick={() => nav("/home")}
            data-testid="pipeline-back"
            className="text-white hover:bg-white/10 flex-shrink-0"
          >
            <ArrowLeft size={16} className="sm:mr-1" />
            <span className="hidden sm:inline">Home</span>
          </Button>
          <div className="min-w-0 flex-1">
            <div className="text-base sm:text-xl font-bold tracking-tight leading-none truncate">PR Pipeline</div>
            <MirrorFreshness syncInfo={syncInfo} />
          </div>
          <Button
            variant="ghost" size="sm" onClick={load}
            data-testid="pipeline-refresh"
            className="text-white hover:bg-white/10 flex-shrink-0"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </Button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Stage tabs — also act as count badges. */}
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-4" data-testid="pipeline-stage-tabs">
          {STAGE_TABS.map((t) => {
            const active = stage === t.code;
            const c = COLOR_MAP[t.color];
            const cnt = stageCounts[t.code] ?? "—";
            return (
              <button
                key={t.code}
                onClick={() => setStage(t.code)}
                data-testid={`pipeline-tab-${t.code}`}
                className={`px-3 py-1.5 rounded-sm text-xs sm:text-sm font-semibold border-2 transition-all ${
                  active
                    ? `${c.pill} border-transparent shadow-md`
                    : `bg-white text-slate-700 ${c.border} hover:${c.bg}`
                }`}
              >
                {t.label}
                <span className={`ml-1.5 inline-flex items-center justify-center text-[10px] font-bold tabular-nums px-1.5 py-0 rounded-sm ${
                  active ? "bg-white/20" : `${c.bg} ${c.text}`
                }`}>
                  {cnt}
                </span>
              </button>
            );
          })}

          {/* AI-flagged HOLD chip — sits alongside the stage tabs but acts as a
              cross-cutting filter: it can be combined with any stage tab.
              Highlights PRs the risk engine recommended HOLD but no human has
              acted on yet (so they still sit in their real workflow stage). */}
          <button
            onClick={toggleAiHold}
            data-testid="pipeline-chip-ai-hold"
            aria-pressed={aiHold}
            title="Show only PRs where the AI risk engine recommended HOLD (still awaiting human review)"
            className={`ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs sm:text-sm font-semibold border-2 transition-all ${
              aiHold
                ? "bg-red-700 text-white border-transparent shadow-md"
                : "bg-white text-red-700 border-red-300 hover:bg-red-50"
            }`}
          >
            <AlertTriangle size={13} />
            AI-Flagged HOLD
            <span className={`inline-flex items-center justify-center text-[10px] font-bold tabular-nums px-1.5 py-0 rounded-sm ${
              aiHold ? "bg-white/20" : "bg-red-100 text-red-700"
            }`}>
              {aiHoldCount}
            </span>
          </button>
        </div>

        {/* Filter row — PR # / supplier, Requestor, Creator */}
        <div className="bg-white border border-slate-200 rounded-sm p-3 sm:p-4 mb-4">
          <datalist id="pipeline-supplier-suggest">
            {supplierSuggestions.map((s) => <option key={s} value={s} />)}
          </datalist>
          <datalist id="pipeline-requestor-suggest">
            {requestorSuggestions.map((s) => <option key={s} value={s} />)}
          </datalist>
          <datalist id="pipeline-creator-suggest">
            {creatorSuggestions.map((s) => <option key={s} value={s} />)}
          </datalist>
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-3">
            <div className="sm:col-span-4">
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">PR # or Supplier</label>
              <div className="relative">
                <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Start typing — supplier or PR #"
                  list="pipeline-supplier-suggest"
                  className="pl-7 h-9 text-sm"
                  data-testid="pipeline-search-q"
                />
              </div>
            </div>
            <div className="sm:col-span-3">
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">Requestor</label>
              <Input
                value={requestor}
                onChange={(e) => setRequestor(e.target.value)}
                placeholder="Start typing a name…"
                list="pipeline-requestor-suggest"
                className="h-9 text-sm"
                data-testid="pipeline-search-requestor"
              />
            </div>
            <div className="sm:col-span-3">
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">
                Creator (who pressed Submit)
              </label>
              <Input
                value={creator}
                onChange={(e) => setCreator(e.target.value)}
                placeholder="Start typing a name…"
                list="pipeline-creator-suggest"
                className="h-9 text-sm"
                data-testid="pipeline-search-creator"
              />
            </div>
            <div className="sm:col-span-2 flex items-end gap-2">
              {(q || requestor || creator) ? (
                <Button variant="outline" onClick={clearFilters} className="h-9 flex-1" data-testid="pipeline-clear-filters">
                  Clear filters
                </Button>
              ) : (
                <div className="h-9 flex-1 inline-flex items-center justify-center text-[11px] text-slate-400 italic">
                  filters apply live
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 mt-2">
            <div className="text-xs text-slate-500 flex items-center gap-1">
              <Clock size={11} /> Includes all Open PRs + closed PRs from the last 30 days · org-wide, read-only.
            </div>
            {user?.emp_no && (
              <button
                onClick={setJustMine}
                aria-pressed={justMineActive}
                title="Show only PRs I created"
                data-testid="pipeline-just-mine"
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-[11px] font-semibold border transition-all ${
                  justMineActive
                    ? "bg-cyan-700 text-white border-transparent"
                    : "bg-white text-cyan-700 border-cyan-300 hover:bg-cyan-50"
                }`}
              >
                Just mine
              </button>
            )}
          </div>
        </div>

        {/* The table */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pipeline-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-600 font-bold">
                  <SortableTh label="PR #"          k="voc_no"         sort={sort} onClick={toggleSort} />
                  <SortableTh label="Date"          k="vdate"          sort={sort} onClick={toggleSort} />
                  <SortableTh label="Plant"         k="plant_name"     sort={sort} onClick={toggleSort} />
                  <SortableTh label="Supplier"      k="supplier_name"  sort={sort} onClick={toggleSort} />
                  <SortableTh label="Amount"        k="amount"         sort={sort} onClick={toggleSort} align="right" />
                  <SortableTh label="Requestor"     k="requestor_name" sort={sort} onClick={toggleSort} />
                  <SortableTh label="Stage"         k="stage_label"    sort={sort} onClick={toggleSort} />
                  <SortableTh label="Pending With"  k="pending_with"   sort={sort} onClick={toggleSort} />
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500" data-testid="pipeline-loading">Loading…</td></tr>
                )}
                {!loading && visibleRows.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-12 text-center text-slate-500" data-testid="pipeline-empty">
                    No PRs match the current filters.
                  </td></tr>
                )}
                {!loading && visibleRows.map((r) => (
                  <tr
                    key={r.voc_no}
                    onClick={() => setOpenVoc(r.voc_no)}
                    className="border-b border-slate-100 hover:bg-cyan-50/50 cursor-pointer transition-colors"
                    data-testid={`pipeline-row-${r.voc_no}`}
                  >
                    <td className="px-3 py-2.5 font-mono text-xs font-semibold text-slate-900">{r.voc_no}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">{fmtDate(r.vdate)}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-700 max-w-[160px] truncate"
                        title={r.plant ? `${r.plant_name || r.plant} (${r.plant})` : ""}>
                      {r.plant_name || r.plant || "—"}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-800 max-w-[240px] truncate" title={r.supplier_name || ""}>
                      {r.supplier_name || "—"}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-right tabular-nums font-medium text-slate-900">
                      {fmtMoney(r.amount, r.currency)}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-700 max-w-[160px] truncate" title={r.requestor_name || ""}>
                      {r.requestor_name || r.create_by_name || "—"}
                    </td>
                    <td className="px-3 py-2.5">
                      <StageBadge code={r.stage} label={r.stage_label} color={r.stage_color} />
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-700 max-w-[200px] truncate" title={r.pending_with || ""}>
                      {r.pending_with || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!loading && visibleRows.length > 0 && (
            <div className="px-3 py-2 border-t border-slate-100 text-[11px] text-slate-500 flex items-center justify-between">
              <span><span className="font-semibold text-slate-700 tabular-nums">{visibleRows.length}</span> PR{visibleRows.length === 1 ? "" : "s"} shown</span>
              <span>Click any row to see full PR details</span>
            </div>
          )}
        </div>

        <div className="text-center mt-6 text-xs text-slate-500">
          Logged in as <span className="font-semibold text-slate-700">{user.username || user.full_name}</span> · This view is read-only.
          Approval actions are available on the Approval Dashboard.
        </div>
      </main>

      {/* Drawer opens read-only — non-authorisers don't see action buttons
          anywhere; authorisers also see no buttons here, by design of the
          Pipeline page (Dashboard is the action workspace). */}
      {openVoc != null && (
        <PRDetailDrawer
          open={openVoc != null}
          vocNo={openVoc}
          onClose={() => setOpenVoc(null)}
          onDecided={() => { setOpenVoc(null); load(); }}
          readOnly
        />
      )}
    </div>
  );
}
